import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  AddCircleOutline,
  Delete,
  DragIndicator,
  ExpandMore,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import Draggable from "../../../../../../components/core/Draggable/Draggable";
import DraggableList from "../../../../../../components/core/Draggable/DraggableList";
import {
  ButtonGroupFormElement,
  ButtonGroupFormElementExecution,
  FormDefinitionType,
  FormElementType,
} from "../../../../types";
import {
  ButtonGroupFunction,
  ButtonGroupSettings,
} from "../../../task-types/form/wizard-form-elements/TaskButtonGroupFormComponent";
import { FormElementChangeType } from "../../FormBuilder";
import { useFormBuilderContext } from "../../FormBuilderProvider";

type Rule = {
  required: boolean;
  type?: "string" | "number" | undefined;
  label?: string;
};

const ButtonGroupFunctionLabels: Record<ButtonGroupFunction, string> = {
  [ButtonGroupFunction.GOTO_STEP]: "Go to Step",
  [ButtonGroupFunction.NEXT_STEP]: "Next Step",
  [ButtonGroupFunction.PREV_STEP]: "Previous Step",
  [ButtonGroupFunction.SUBMIT_TASK]: "Submit Task",
};

const rules: Record<ButtonGroupFunction, Rule> = {
  [ButtonGroupFunction.GOTO_STEP]: {
    required: true,
    type: "number",
    label: "Step Number",
  },
  [ButtonGroupFunction.NEXT_STEP]: {
    required: false,
  },
  [ButtonGroupFunction.PREV_STEP]: {
    required: false,
  },
  [ButtonGroupFunction.SUBMIT_TASK]: {
    required: false,
  },
};

interface StepOption {
  name: string;
  index: number;
}

export const ButtonGroupSideSectionDisplay = ({
  ...props
}: {
  element: ButtonGroupFormElement;
  executionData?: ButtonGroupFormElementExecution;
  onChange: (props: FormElementChangeType) => void;
}) => {
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const { elements } = useFormBuilderContext();

  const options = useMemo(() => {
    const arr: Array<StepOption> = [];
    elements?.forEach((e) => {
      if (e.type === FormElementType.Heading) {
        arr.push({
          name: e.label,
          index: arr.length,
        });
      }
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = (
      props.element.settings as Array<ButtonGroupSettings>
    ).findIndex((a) => a.id === active.id);
    const newIndex = (
      props.element.settings as Array<ButtonGroupSettings>
    ).findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newSettings = Array.from(
      props.element.settings as Array<ButtonGroupSettings>,
    );
    const [moved] = newSettings.splice(oldIndex, 1);
    newSettings.splice(newIndex, 0, moved);
    props.onChange({
      changeType: "update",
      newElement: {
        ...props.element,
        settings: newSettings,
      } as FormDefinitionType,
    });
  };

  const ButtonItemHeader = ({
    settings,
    index,
    openIndexes,
    toggleCollapse,
    onDelete,
  }: {
    settings: ButtonGroupSettings;
    index: number;
    openIndexes: number[];
    toggleCollapse: (index: number) => void;
    onDelete: () => void;
  }) => {
    const isOpen = openIndexes.includes(index);
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onClick={() => toggleCollapse(index)}
        >
          <ExpandMore
            sx={{
              transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s",
            }}
          />
          <Typography>{settings.label || "Unnamed Button"}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size={"small"} onClick={onDelete}>
            <Delete />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              cursor: isOpen ? "not-allowed" : "grab",
              pointerEvents: isOpen ? "none" : "auto",
              opacity: isOpen ? 0.5 : 1,
            }}
            tabIndex={-1}
            disableRipple
            disabled={isOpen}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const activeSettings = (
    props.element.settings as Array<ButtonGroupSettings>
  ).find((s) => s.id === activeDragId);
  const activeIndex = (
    props.element.settings as Array<ButtonGroupSettings>
  ).findIndex((s) => s.id === activeDragId);
  const dragOverlay =
    activeSettings !== undefined ? (
      <Paper
        elevation={1}
        sx={(t) => ({
          p: 1,
          background: t.palette.grey[100],
          border: `1px solid ${t.palette.grey[300]}`,
          borderRadius: "8px",
          minWidth: 200,
        })}
      >
        <ButtonItemHeader
          settings={activeSettings}
          index={activeIndex}
          openIndexes={openIndexes}
          toggleCollapse={() => {}}
          onDelete={() => {}}
        />
      </Paper>
    ) : null;

  const toggleCollapse = (index: number) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const handleAlignmentChange = (newAlignment: "left" | "right" | "center") => {
    props.onChange({
      changeType: "update",
      newElement: {
        ...props.element,
        alignment: newAlignment,
      } as ButtonGroupFormElement,
    });
  };

  const alignment = (props.element as ButtonGroupFormElement).alignment;

  return (
    <>
      <Divider />
      {/* Button Alignment */}
      <Typography variant="body1">Button Alignment</Typography>
      <Box
        sx={(t) => ({
          display: "flex",
          justifyContent: "space-between",
          mt: 0,
          background: t.palette.grey[100],
          border: `1px solid ${t.palette.grey[300]}`,
          p: 1,
          borderRadius: "25px",
        })}
      >
        <Button
          variant={alignment === "left" ? "contained" : "outlined"}
          color="inherit"
          onClick={() => handleAlignmentChange("left")}
          startIcon={<FormatAlignLeft />}
          sx={{ flexGrow: 1, border: 0 }}
        >
          Left
        </Button>
        <Button
          variant={alignment === "center" ? "contained" : "outlined"}
          color="inherit"
          onClick={() => handleAlignmentChange("center")}
          startIcon={<FormatAlignCenter />}
          sx={{ flexGrow: 1, border: 0 }}
        >
          Center
        </Button>
        <Button
          variant={alignment === "right" ? "contained" : "outlined"}
          color="inherit"
          onClick={() => handleAlignmentChange("right")}
          startIcon={<FormatAlignRight />}
          sx={{ flexGrow: 1, border: 0 }}
        >
          Right
        </Button>
      </Box>

      <Divider />

      {/* Button Editing */}
      <Typography variant="body1" sx={{ mb: 2 }}>
        Buttons
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {props.element.settings && (
          <DraggableList
            listItemIds={(
              props.element.settings as Array<ButtonGroupSettings>
            ).map((s) => s.id)}
            handleDragEnd={handleDragEnd}
            handleDragStart={handleDragStart}
            overlayComponent={dragOverlay}
          >
            {(props.element.settings as Array<ButtonGroupSettings>).map(
              (settings, index) => {
                const isOpen = openIndexes.includes(index);
                const paperContent = (
                  <Paper
                    elevation={1}
                    sx={(t) => ({
                      p: 1,
                      background: t.palette.grey[100],
                      border: `1px solid ${t.palette.grey[300]}`,
                      borderRadius: "8px",
                    })}
                  >
                    <ButtonItemHeader
                      settings={settings}
                      index={index}
                      openIndexes={openIndexes}
                      toggleCollapse={toggleCollapse}
                      onDelete={() => {
                        props.onChange({
                          changeType: "update",
                          newElement: {
                            ...props.element,
                            settings: (
                              props.element
                                .settings as Array<ButtonGroupSettings>
                            ).filter((a) => a.id !== settings.id),
                          } as FormDefinitionType,
                        });
                      }}
                    />
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          mt: 1,
                          p: 0.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Set Button Label */}
                        <TextField
                          fullWidth
                          label="Button Label"
                          value={
                            (
                              props.element
                                .settings as Array<ButtonGroupSettings>
                            )[index].label
                          }
                          onChange={(e) => {
                            props.onChange({
                              changeType: "update",
                              newElement: {
                                ...props.element,
                                settings: (
                                  props.element
                                    .settings as Array<ButtonGroupSettings>
                                ).map((a) =>
                                  a.id === settings.id
                                    ? { ...a, label: e.target.value }
                                    : a,
                                ),
                              } as FormDefinitionType,
                            });
                          }}
                        />
                        {/* Set Button Assignment */}
                        <Autocomplete
                          options={Object.values(ButtonGroupFunction).map(
                            (value) => ({
                              label: ButtonGroupFunctionLabels[value],
                              value,
                            }),
                          )}
                          getOptionLabel={(option) => option.label}
                          value={{
                            label:
                              ButtonGroupFunctionLabels[settings.assignment],
                            value: settings.assignment,
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          onChange={(event, newValue) => {
                            if (newValue) {
                              props.onChange({
                                changeType: "update",
                                newElement: {
                                  ...props.element,
                                  settings: (
                                    props.element
                                      .settings as Array<ButtonGroupSettings>
                                  ).map((a) =>
                                    a.id === settings.id
                                      ? { ...a, assignment: newValue.value }
                                      : a,
                                  ),
                                } as FormDefinitionType,
                              });
                            }
                          }}
                          disableClearable
                          renderInput={(params) => (
                            <TextField {...params} label="Button Action" />
                          )}
                        />
                        {/* Set text field data */}
                        {rules[settings.assignment].required &&
                        settings.assignment ===
                          ButtonGroupFunction.GOTO_STEP ? (
                          <Autocomplete
                            options={options}
                            getOptionLabel={(option) => option.name}
                            value={
                              options.find(
                                (opt) => opt.index === settings.data,
                              ) || null
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.index === value.index
                            }
                            onChange={(event, newValue) => {
                              props.onChange({
                                changeType: "update",
                                newElement: {
                                  ...props.element,
                                  settings: (
                                    props.element
                                      .settings as Array<ButtonGroupSettings>
                                  ).map((a) =>
                                    a.id === settings.id
                                      ? {
                                          ...a,
                                          data: newValue
                                            ? newValue.index
                                            : undefined,
                                        }
                                      : a,
                                  ),
                                } as FormDefinitionType,
                              });
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Go to Step" />
                            )}
                          />
                        ) : rules[settings.assignment].required ? (
                          <TextField
                            label={rules[settings.assignment].label}
                            type={
                              rules[settings.assignment].type === "number"
                                ? "number"
                                : "text"
                            }
                            value={settings.data ?? ""}
                            onChange={(e) => {
                              const value =
                                rules[settings.assignment].type === "number"
                                  ? e.target.value === ""
                                    ? ""
                                    : Number(e.target.value)
                                  : e.target.value;
                              props.onChange({
                                changeType: "update",
                                newElement: {
                                  ...props.element,
                                  settings: (
                                    props.element
                                      .settings as Array<ButtonGroupSettings>
                                  ).map((a) =>
                                    a.id === settings.id
                                      ? { ...a, data: value }
                                      : a,
                                  ),
                                } as FormDefinitionType,
                              });
                            }}
                          />
                        ) : null}
                        {/* Set Button Variant */}
                        <Autocomplete
                          options={["contained", "outlined", "text"]}
                          getOptionLabel={(option) =>
                            option.charAt(0).toUpperCase() + option.slice(1)
                          }
                          value={settings.variant || "contained"}
                          onChange={(event, newValue) => {
                            props.onChange({
                              changeType: "update",
                              newElement: {
                                ...props.element,
                                settings: (
                                  props.element
                                    .settings as Array<ButtonGroupSettings>
                                ).map((a) =>
                                  a.id === settings.id
                                    ? { ...a, variant: newValue }
                                    : a,
                                ),
                              } as FormDefinitionType,
                            });
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Button Variant" />
                          )}
                        />
                      </Box>
                    </Collapse>
                  </Paper>
                );
                return isOpen ? (
                  <Box key={settings.id}>{paperContent}</Box>
                ) : (
                  <Draggable key={settings.id} id={settings.id}>
                    {paperContent}
                  </Draggable>
                );
              },
            )}
          </DraggableList>
        )}
      </Box>
      {/* Create Button */}
      <Button
        color="inherit"
        startIcon={<AddCircleOutline />}
        onClick={() =>
          props.onChange({
            changeType: "update",
            newElement: {
              ...props.element,
              settings: [
                ...(props.element.settings as Array<ButtonGroupSettings>),
                {
                  id: uuid(),
                  label: "Unnamed",
                  assignment: ButtonGroupFunction.NEXT_STEP,
                  data: 1,
                },
              ],
            } as FormDefinitionType,
          })
        }
      >
        Add Button
      </Button>
    </>
  );
};
